import { Button, Css, FullBleed, HbLoadingSpinner, RouteTabWithContent, Tabs, Tag, useModal } from "@homebound/beam";
import { createPlanPackagesUrl } from "src/RouteUrls";
import { ReadyPlanVersionHeader } from "src/components/ReadyPlanVersionHeader";
import { ReadyPlanStatus } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { PageHeader } from "src/routes/layout/PageHeader";
import { PlanPackageDetailsFragment } from "src/routes/libraries/plan-package/details/PlanPackagePage.generated";
import { PlanPackageButtonMenu } from "src/routes/libraries/plan-package/details/components/PlanPackageButtonMenu";
import { PublishAggregateVersionModal } from "src/routes/libraries/plan-package/details/components/PublishAggregateVersionModal";
import { PlanPackageDetailsParams } from "src/routes/routesDef";
import { fail } from "src/utils";

type PlanPackageHeaderProps = { planPackage: PlanPackageDetailsFragment; tabs: RouteTabWithContent[] };

export function PlanPackageHeader({ planPackage, tabs }: PlanPackageHeaderProps) {
  const { openModal } = useModal();
  const { planPackageVersionId } = useRouteParams<PlanPackageDetailsParams>();
  const { id, name, constructionDocumentCompletePercentage, status, aggregateDraft } = planPackage;

  return (
    <FullBleed>
      <div css={Css.bgWhite.bb.bw1.bcGray200.$}>
        {/* Max-width based on Figma designs */}
        <div css={Css.maxwPx(1156).mxa.$}>
          <PageHeader
            title={name}
            titleSize="xl2"
            noBorder
            xss={Css.mb0.$}
            left={
              <>
                {status.code === ReadyPlanStatus.Archived && <Tag text="Archived" type="warning" />}
                <span css={Css.smSb.gray800.$}>{constructionDocumentCompletePercentage}% CD</span>
              </>
            }
            right={
              <>
                {planPackage.cloneInProgress && (
                  <div css={Css.df.fdr.aic.mdSb.red400.pr4.gap2.$}>
                    <div css={Css.maxwPx(64).maxhPx(64).$}>
                      <HbLoadingSpinner iconOnly />
                    </div>
                    Clone in progress...
                  </div>
                )}
                <ReadyPlanVersionHeader readyPlan={planPackage} versionId={planPackageVersionId} />
                {/* TODO: Work with Product & Design to determine when/how this can be Published */}
                <Button
                  size="md"
                  disabled={!aggregateDraft?.canPublish.allowed}
                  tooltip={disableBasedOnPotentialOperation(aggregateDraft?.canPublish)}
                  label="Publish"
                  onClick={() =>
                    openModal({
                      content: (
                        <PublishAggregateVersionModal
                          draftAggregateVersion={aggregateDraft ?? fail("No draft aggregate version found")}
                          copyText="This update will be made available to any Design Package and Product Offering that reference it."
                          readyPlanName={name}
                          readyPlanId={id}
                          allowGenerateAiNotes
                        />
                      ),
                    })
                  }
                />
                <PlanPackageButtonMenu planPackage={planPackage} iconTrigger redirectOnDiscardDraft />
              </>
            }
            breadcrumb={[{ label: "Plans", href: createPlanPackagesUrl() }]}
          />
          <Tabs tabs={tabs} />
        </div>
      </div>
    </FullBleed>
  );
}
